import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import { AdminTool } from '../tools-catalog/catalogModel';

interface ToolUser { userId: number; email: string; displayName?: string; isEnabled: boolean }

const initials = (user: ToolUser) =>
    (user.displayName || user.email).split(/[\s.@]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/** Chi può usare il tool: spiegazione della visibilità ed elenco degli utenti abilitati. */
const AccessSection: React.FC<{ tool: AdminTool; onCountChange: (count: number) => void }> = ({ tool, onCountChange }) => {
    const { showToast } = useToast();
    const [users, setUsers] = useState<ToolUser[] | null>(null);
    const [search, setSearch] = useState('');
    const [onlyEnabled, setOnlyEnabled] = useState(false);

    useEffect(() => {
        apiService.adminGetToolUsers<ToolUser>(tool.id).then(setUsers).catch(() => setUsers([]));
    }, [tool.id]);

    const enabled = users?.filter(u => u.isEnabled).length ?? 0;
    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (users ?? [])
            .filter(u => !onlyEnabled || u.isEnabled)
            .filter(u => !q || `${u.displayName ?? ''} ${u.email}`.toLowerCase().includes(q));
    }, [users, search, onlyEnabled]);

    const toggle = async (user: ToolUser) => {
        const next = !user.isEnabled;
        const apply = (value: boolean) => {
            const list = (users ?? []).map(u => (u.userId === user.userId ? { ...u, isEnabled: value } : u));
            setUsers(list);
            onCountChange(list.filter(u => u.isEnabled).length);
        };
        apply(next);
        try {
            await apiService.adminSetUserToolAccess(tool.id, user.userId, next);
            showToast(next ? 'Accesso abilitato' : 'Accesso tolto', user.displayName || user.email, 'success');
        } catch {
            apply(!next);
            showToast('Accesso non modificato', user.displayName || user.email, 'danger');
        }
    };

    return (
        <section className="td-card td-access" aria-labelledby="td-access-title">
            <h2 id="td-access-title" className="td-card__title">Chi può usare il tool</h2>
            <p className="td-card__lead">
                {tool.visibility === 'Restricted'
                    ? 'Su invito: lo vedono solo gli utenti abilitati qui sotto.'
                    : 'Tutti gli utenti: lo vede chiunque abbia un account approvato. Gli utenti abilitati qui sotto restano tali se il tool passa su invito.'}
                {' '}La visibilità si cambia dall’intestazione.
            </p>
            <div className="td-access__toolbar">
                <label htmlFor="td-user-search" className="visually-hidden">Cerca un utente</label>
                <input id="td-user-search" type="search" className="form-control" placeholder="Cerca per nome o email"
                    value={search} onChange={e => setSearch(e.target.value)} />
                <label className="td-access__filter">
                    <input type="checkbox" className="form-check-input" checked={onlyEnabled} onChange={e => setOnlyEnabled(e.target.checked)} />
                    Solo abilitati
                </label>
                <span className="td-access__count">{users ? `${enabled} di ${users.length} utenti abilitati` : 'Caricamento…'}</span>
            </div>
            {users && visible.length === 0 && (
                <p className="td-card__note">{users.length ? 'Nessun utente corrisponde alla ricerca.' : 'Non ci sono ancora utenti registrati.'}</p>
            )}
            <ul className="td-access__list">
                {visible.map(user => (
                    <li key={user.userId}>
                        <span className="td-access__avatar" aria-hidden="true">{initials(user)}</span>
                        <span className="td-access__who">
                            <strong>{user.displayName || user.email}</strong>
                            {user.displayName && <span>{user.email}</span>}
                        </span>
                        <label className="td-access__switch">
                            <input type="checkbox" role="switch" className="form-check-input" checked={user.isEnabled}
                                onChange={() => void toggle(user)} aria-label={`Abilita ${user.displayName || user.email}`} />
                            {user.isEnabled ? 'Abilitato' : 'Non abilitato'}
                        </label>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default AccessSection;
